/**
 * Removes the slots of an aligned set of spectra that too few spectra contain.
 *
 * `requiredY`, `minNumberOfSpectra` and `minConsecutiveSpectra` are the criteria,
 * and `minY` only says what they count as contained. They may be combined, a slot
 * is kept only when it passes all the ones that are set, so with no criterion set
 * nothing is removed. They only ever remove slots, the y values are never changed.
 * @param data - aligned spectra, as returned by the align methods.
 * @param options - options.
 * @returns the same spectra with the rejected slots removed.
 */
export function xysFilter(data, options = {}) {
    const { minY, minNumberOfSpectra = 0, minConsecutiveSpectra = 0, requiredY = false, } = options;
    const { x, ys } = data;
    const length = x.length;
    const numberOfSpectra = ys.length;
    const minNumber = requiredY ? numberOfSpectra : minNumberOfSpectra;
    const hasMinY = minY !== undefined;
    const threshold = minY ?? 0;
    const numbers = new Uint32Array(length);
    let longestRuns;
    if (minConsecutiveSpectra > 0) {
        longestRuns = new Uint32Array(length);
        const runs = new Uint32Array(length);
        for (let j = 0; j < numberOfSpectra; j++) {
            const y = ys[j];
            for (let i = 0; i < length; i++) {
                if (hasMinY ? y[i] > threshold : y[i] !== 0) {
                    numbers[i]++;
                    const run = runs[i] + 1;
                    runs[i] = run;
                    if (run > longestRuns[i])
                        longestRuns[i] = run;
                }
                else {
                    runs[i] = 0;
                }
            }
        }
    }
    else if (minNumber > 0) {
        for (let j = 0; j < numberOfSpectra; j++) {
            const y = ys[j];
            for (let i = 0; i < length; i++) {
                if (hasMinY ? y[i] > threshold : y[i] !== 0)
                    numbers[i]++;
            }
        }
    }
    let kept = 0;
    for (let i = 0; i < length; i++) {
        if (numbers[i] < minNumber)
            continue;
        if (longestRuns && longestRuns[i] < minConsecutiveSpectra)
            continue;
        kept++;
    }
    const newX = new Float64Array(kept);
    const positions = new Uint32Array(kept);
    let at = 0;
    for (let i = 0; i < length; i++) {
        if (numbers[i] < minNumber)
            continue;
        if (longestRuns && longestRuns[i] < minConsecutiveSpectra)
            continue;
        newX[at] = x[i];
        positions[at] = i;
        at++;
    }
    const newYs = Array.from(ys, (y) => {
        const newY = new Float64Array(kept);
        for (let i = 0; i < kept; i++) {
            newY[i] = y[positions[i]];
        }
        return newY;
    });
    return { x: newX, ys: newYs };
}
//# sourceMappingURL=xysFilter.js.map